import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Controller, useForm } from "react-hook-form";
import CustomeButton from "~/components/Button";
import { currencyList } from "~/utils/currency";
import PropTypes from "prop-types";
import {
  formatDateToISOStartOfDay,
  removeTimezoneFromDateString,
} from "~/utils/dateUtil";

const AddTransactionModal = ({
  visible,
  transaction,
  setTransaction,
  setShareRegistryData,
  edit,
  rowIndex,
  closeModal,
  isEditing,
  updateTransaction,
  addRegistryTransaction,
  loading,
  isBtnLoading,
}) => {
  const TRANSACTION_TYPE = [
    { label: "Purchase", value: "Purchase" },
    { label: "Sale", value: "Sale" },
    { label: "Transfer", value: "Transfer" },
  ];
  const SHARE_CLASS = [
    { label: "A", value: "A" },
    { label: "B", value: "B" },
    { label: "C", value: "C" },
    { label: "D", value: "D" },
    { label: "Rm Z Shares", value: "Rm Z Shares" },
  ];
  const {
    control,
    clearErrors,
    formState: { errors, isDirty, isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      type: transaction.type || "",
      acquisition_date: transaction.acquisition_date || "",
      number_of_shares: transaction.number_of_shares || "",
      share_value: transaction.share_value || "",
      currency: transaction.currency || "",
      share_percentage: transaction.share_percentage || "",
      share_class: transaction.share_class || "",
    },
  });

  const addTransaction = () => {
    setShareRegistryData((prev) => [...prev, transaction]);
    closeModalHandler();
  };

  const editTransaction = () => {
    setShareRegistryData((prev) =>
      prev.map((item, i) =>
        i === rowIndex ? { ...item, ...transaction } : item,
      ),
    );
    closeModalHandler();
  };

  const closeModalHandler = () => {
    clearErrors();
    closeModal();
  };

  return (
    <Dialog
      visible={visible}
      className="transaction-modal"
      onHide={closeModalHandler}
    >
      <div className="transaction-modal-container">
        <div className="flex flex-column gap-3">
          <div className="content-header flex flex-column gap-1">
            <p className="title">
              {" "}
              {edit ? "Edit Share Transaction" : "Add Share Transaction"}
            </p>
            <p className="description">
              Fill in the transaction details below and hit add when ready!
            </p>
          </div>

          <div className="formgrid grid ">
            <div className="field col-12 general-info-margin">
              <label className="input-label">Share Transaction Type *</label>
              <Controller
                name="type"
                control={control}
                rules={{ required: "Share Transaction Type is required" }}
                render={({ field }) => {
                  return (
                    <Dropdown
                      {...field}
                      placeholder="Select transaction type"
                      className={`w-full dropdown-field `}
                      options={TRANSACTION_TYPE}
                      value={edit ? transaction.type : field.value}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        setTransaction((prev) => ({
                          ...prev,
                          type: e.target.value,
                        }));
                      }}
                    />
                  );
                }}
              />
              {errors.type && (
                <small className="error-message">{errors.type.message}</small>
              )}
            </div>

            <div
              className={`field col-12  ${errors.acquisition_date ? "mb-3" : "mb-2"}`}
            >
              <label className="input-label">Acquisition Date *</label>
              <Controller
                name="acquisition_date"
                control={control}
                rules={{ required: "Acquisition date is required" }}
                render={({ field }) => {
                  return (
                    <>
                      <Calendar
                        {...field}
                        placeholder="MM/DD/YY"
                        className={`w-full calendar-field`}
                        value={
                          edit
                            ? removeTimezoneFromDateString(
                                transaction.acquisition_date,
                              )
                            : field.value &&
                              removeTimezoneFromDateString(field.value)
                        }
                        onChange={(e) => {
                          const newDate = formatDateToISOStartOfDay(e.value);
                          field.onChange(newDate);
                          setTransaction((prev) => ({
                            ...prev,
                            acquisition_date: newDate,
                          }));
                        }}
                      />
                      <span className="pi pi-calendar acquisition-date-icon"></span>
                    </>
                  );
                }}
              />
              {errors.acquisition_date && (
                <small className="error-message date-error-message">
                  {errors.acquisition_date.message}
                </small>
              )}
            </div>

            <div className="field col-6 general-info-margin">
              <label className="input-label">Number of Shares *</label>
              <Controller
                name="number_of_shares"
                control={control}
                rules={{ required: "Number of Shares is required" }}
                render={({ field }) => {
                  return (
                    <InputText
                      {...field}
                      placeholder="Enter number of shares"
                      className={`input-field appearance-none outline-none w-full `}
                      value={edit ? transaction.number_of_shares : field.value}
                      onChange={(e) => {
                        if (/^\d*\.?\d*$/.test(e.target.value)) {
                          field.onChange(e.target.value);
                          setTransaction((prev) => ({
                            ...prev,
                            number_of_shares: e.target.value,
                          }));
                        }
                      }}
                    />
                  );
                }}
              />
              {errors.number_of_shares && (
                <small className="error-message">
                  {errors.number_of_shares.message}
                </small>
              )}
            </div>

            <div className="field col-6 mb-0">
              <label className="input-label">Share Value *</label>
              <div className="flex">
                <div className="field col-6 ">
                  <Controller
                    name="currency"
                    control={control}
                    rules={{ required: "Currency is required" }}
                    render={({ field }) => {
                      return (
                        <Dropdown
                          {...field}
                          placeholder="Currency"
                          className={`w-full dropdown-field `}
                          options={currencyList}
                          filter
                          filterBy="label,value"
                          value={edit ? transaction.currency : field.value}
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            setTransaction((prev) => ({
                              ...prev,
                              currency: e.target.value,
                            }));
                          }}
                        />
                      );
                    }}
                  />
                  {errors.currency && (
                    <small className="error-message">
                      {errors.currency.message}
                    </small>
                  )}
                </div>

                <div className="field col-6 ">
                  <Controller
                    name="share_value"
                    control={control}
                    rules={{ required: "Value is required" }}
                    render={({ field }) => {
                      return (
                        <InputText
                          {...field}
                          placeholder="Enter value"
                          className={`input-field appearance-none outline-none w-full `}
                          value={edit ? transaction.share_value : field.value}
                          onChange={(e) => {
                            if (/^\d*\.?\d*$/.test(e.target.value)) {
                              field.onChange(e.target.value);
                              setTransaction((prev) => ({
                                ...prev,
                                share_value: e.target.value,
                              }));
                            }
                          }}
                        />
                      );
                    }}
                  />
                  {errors.share_value && (
                    <small className="error-message">
                      {errors.share_value.message}
                    </small>
                  )}
                </div>
              </div>
            </div>

            <div className="field col-12 general-info-margin">
              <label className="input-label">Shareholding Percentage*</label>
              <Controller
                name="share_percentage"
                rules={{ required: "Share Percentage is required" }}
                control={control}
                render={({ field }) => {
                  return (
                    <InputText
                      {...field}
                      placeholder="% Enter shareholding percentage"
                      className={`input-field appearance-none outline-none w-full `}
                      value={edit ? transaction.share_percentage : field.value}
                      onChange={(e) => {
                        if (/^\d*\.?\d*$/.test(e.target.value)) {
                          field.onChange(e.target.value);
                          setTransaction((prev) => ({
                            ...prev,
                            share_percentage: e.target.value,
                          }));
                        }
                      }}
                    />
                  );
                }}
              />
              {errors.share_percentage && (
                <small className="error-message">
                  {errors.share_percentage.message}
                </small>
              )}
            </div>

            <div className="field col-12 general-info-margin">
              <label className="input-label">Share Class</label>
              <Controller
                name="share_class"
                control={control}
                render={({ field }) => {
                  return (
                    <Dropdown
                      {...field}
                      placeholder="Select share class"
                      className={`w-full dropdown-field `}
                      options={SHARE_CLASS}
                      filter
                      filterBy="value"
                      value={edit ? transaction?.share_class : field.value}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        setTransaction((prev) => ({
                          ...prev,
                          share_class: e.target.value,
                        }));
                      }}
                    />
                  );
                }}
              />
            </div>
          </div>
          {edit ? (
            <div className="flex gap-2 justify-content-end">
              <CustomeButton
                className="cancel-btns"
                onClick={closeModalHandler}
              >
                Discard
              </CustomeButton>
              <CustomeButton
                label="Save Changes"
                variant="contained"
                className={(!isValid || !isDirty) && "disable"}
                disabled={!isValid || !isDirty}
                loading={isEditing && isBtnLoading}
                onClick={() =>
                  isEditing ? updateTransaction(transaction) : editTransaction()
                }
              />
            </div>
          ) : (
            <div className="flex gap-2 justify-content-end">
              <CustomeButton
                className="cancel-btns"
                onClick={closeModalHandler}
              >
                Cancel
              </CustomeButton>
              <CustomeButton
                label="Add Share Transaction"
                variant="contained"
                className={!isValid && "disable"}
                disabled={!isValid}
                loading={isEditing && isBtnLoading}
                onClick={() =>
                  isEditing ? addRegistryTransaction() : addTransaction()
                }
              />
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};

AddTransactionModal.propTypes = {
  visible: PropTypes.bool,
  transaction: PropTypes.object,
  setTransaction: PropTypes.func,
  setShareRegistryData: PropTypes.func,
  edit: PropTypes.bool,
  rowIndex: PropTypes.string,
  closeModal: PropTypes.func,
  sEditing: PropTypes.bool,
  updateTransaction: PropTypes.func,
  addRegistryTransaction: PropTypes.func,
  loading: PropTypes.bool,
  isBtnLoading: PropTypes.bool,
};

export default AddTransactionModal;
